/* global AmplitudeKeyFeatureFlag */

import { Experiment } from '@amplitude/experiment-js-client';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import { isSeries, isEpisode } from 'helpers/ContentHelper';
import { getAnonymousIdCookie, anonymousIdWatcher } from 'helpers/AnonymousHelper';
import { getCookie } from 'helpers/Storage';

export const EXPERIMENTS = {
  MOBILE_WEB_ROADBLOCK: 'mobile-web-roadblock',
  WEB_PLAYER_PLAYLIST: 'web-player-playlist',
  MLP_WEB_BROWSE_CONTENT: 'mlp-web-browse-content',
  WEB_ANON_CTA: 'web-anon-cta',
};

// eslint-disable-next-line no-unused-vars
export const VARIANTS = {
  CONTROL: 'control',
  HARD_ROADBLOCK: 'hard-roadblock',
  SOFT_ROADBLOCK: 'soft-roadblock',
  VARIANT: 'variant',
  VARIANT_1: 'variant-1',
};

export const getVariant = ({ key }) => experimentsDataObj?.[key]?.value;

// eslint-disable-next-line no-unused-vars
let firedExposures = {};
let experimentsDataObj = {};
let amplitudeInstance;
const user = {
  // user_id: '',  // Not sending user ids for now
  device_id: '',
  user_properties: {},
};

export const launchAmplitude = async () => {
  await anonymousIdWatcher();
  amplitudeInstance = Experiment.initialize(AmplitudeKeyFeatureFlag);
  window.amplitude = {};
  await resetAmplitudeUser();
  window.amplitude.experiments = experimentsDataObj;
};

const getPageTestName = ({ key, variant }) => `${key}:${variant}`?.toLowerCase();

const resetAmplitudeUser = async () => {
  // only reset if id changed:
  const newUserDeviceId = getAnonymousIdCookie();
  if (getAmplitudeUser().device_id !== newUserDeviceId) {
    setInitUserAttributes();
    const user = getAmplitudeUser();
    resetExposureHistory();
    await fetchAndCollectExperiments(user);
  }
};

const setInitUserAttributes = () => {
  const deviceId = getAnonymousIdCookie();
  const mlpCookie = getCookie({ name: 'nation_mlp_cmp_var' });
  user.device_id = deviceId;
  user.user_properties = {};
  if (mlpCookie) {
    user.user_properties.mlp_cmp_var = true;
  }
};

const fetchAndCollectExperiments = async user => {
  await fetchExperiments(user);
  resetExperimentsData();
  collectAmplitudeExperiments();
};

const fetchExperiments = async user => {
  const experiments = await amplitudeInstance.fetch(user, { fetchTimeoutMillis: 3000 });
  return experiments;
};

const resetExperimentsData = () => {
  experimentsDataObj = {};
};

const getExperimentVariant = experimentFlagKey => amplitudeInstance.variant(experimentFlagKey);

// Set string of campaigns from Amplitude for eVar65 on the utag for Tealium and use in Segment traits
const collectAmplitudeExperiments = () => {
  const experimentValues = Object.values(EXPERIMENTS);
  experimentValues.forEach(key => {
    const variant = getExperimentVariant(key);
    if (variant.value) {
      experimentsDataObj[key] = variant;
    }
    // exposure call happens on natcom side, we set page test name in this special case
    // if experiment exists, we can delete this when the experiment no longer exists
    const mlpCookie = getCookie({ name: 'nation_mlp_cmp_var' });
    if (key === EXPERIMENTS.MLP_WEB_BROWSE_CONTENT && variant.value && !mlpCookie) {
      window.amplitude.pageTestName = getPageTestName({ key, variant: variant.value });
    }
  });

  const experimentsString = getSegmentAmplitudeString();

  window.utag_data = window.utag_data || {};
  if (experimentsString.length > 0) {
    window.utag_data.user = {
      testname: experimentsString,
    };
  } else {
    window.utag_data.user = {
      testname: undefined,
    };
  }
};

const resetExposureHistory = () => {
  firedExposures = {};
};

// Generate campaign string for Segment from experiments object
const getSegmentAmplitudeString = () => {
  if (experimentsDataObj) {
    const segmentString = Object.entries(experimentsDataObj).map(
      ([experimentKey, variant]) => `${experimentKey.trim()}:${variant.value.trim()}`,
    );
    return segmentString.join(',');
  }
  return '';
};

const getAmplitudeUser = () => user;

export const amplitudeTestName = key => {
  const experiment = experimentsDataObj?.[key];
  const variant = experiment?.value;
  return getPageTestName({ key, variant });
};

export const exposureEvent = ({ experimentKey }) => {
  const experiment = experimentsDataObj?.[experimentKey];
  const variant = experiment?.value;
  const isSessionExposed = window.sessionStorage.getItem(experimentKey);
  const heap = window?.heap;
  if (experiment && !firedExposures[experimentKey] && !isSessionExposed) {
    // Analytics repo uses this to set page_test_name to track calls
    window.amplitude.pageTestName = getPageTestName({ key: experimentKey, variant });
    const payload = {
      flag_key: experimentKey,
      page_test_name: window.amplitude.pageTestName,
      experiment_key: experiment?.expKey,
      variant,
    };
    handleAnalytics({
      event: Events.EXPOSURE,
      payload,
    });
    if (heap.loaded) {
      heap.track('$exposure', payload);
      heap.addUserProperties(payload);
    }
    window.sessionStorage.setItem(experimentKey, true);
    firedExposures[experimentKey] = true;
  }
};

export const getPlaylistVariantActive = ({ panelVideo }) => {
  if (window.location.pathname?.includes('/live')) return false;
  if (isSeries(panelVideo?.seriesType) && isEpisode(panelVideo?.videoType)) {
    const playlistVariant = getVariant({ key: EXPERIMENTS.WEB_PLAYER_PLAYLIST });
    const isDesktopBreakpoint = window.innerWidth >= 1440;
    if (playlistVariant === VARIANTS.CONTROL && isDesktopBreakpoint) {
      exposureEvent({ experimentKey: EXPERIMENTS.WEB_PLAYER_PLAYLIST });
    }
    return playlistVariant === VARIANTS.VARIANT_1 && isDesktopBreakpoint;
  }
  return false;
};
